import { z } from "zod";
import { wellKnownPoses } from "./characters";
import { zChoreo } from "./choreo";
import { zImageReference } from "./imageReferenceSchema";

// A screen receives artwork and geometry, never persona prompts, biographies,
// account references, reward inventory, or character creation tasks. Story item
// snapshots travel separately with the script, projected to public story facts.
const number = z.number().finite();
const scale = number.positive().max(100);
const box = z.object({ left: number, top: number, width: number.nonnegative(), height: number.nonnegative() });
const panel = z.object({
  row: number.int().nonnegative(),
  column: number.int().nonnegative(),
  sprite: box,
  frame: box,
  disabled: z.boolean().optional(),
  constraints: z.object({ top: z.boolean(), bottom: z.boolean(), left: z.boolean(), right: z.boolean() }).optional(),
  // Older artwork used free-form pose names. Geometry still renders correctly
  // when an obsolete semantic hint is omitted.
  pose: z.enum(wellKnownPoses).optional().catch(undefined),
  orientation: z.enum(["left", "center", "right"]).optional(),
  relativeScale: scale.optional(),
});
const panelGrid = z.object({
  columns: number.int().positive(),
  rows: number.int().positive(),
  width: number.positive(),
  height: number.positive(),
});
/**
 * A sheet's named pose sequences, so a shared stage plays a choreo the stager named by id. An entry that does not
 * parse is left out rather than failing the whole cast, and step notes, which only the stager reads, stay behind to
 * keep the snapshot small.
 */
const sheetChoreos = z.preprocess(
  (value) =>
    Array.isArray(value)
      ? value.slice(0, 24).flatMap((entry) => {
          const parsed = zChoreo.safeParse(entry);
          if (!parsed.success || !parsed.data.id) return [];
          return [{ ...parsed.data, poses: parsed.data.poses.map(({ description: _notes, ...step }) => step) }];
        })
      : undefined,
  z.array(zChoreo).max(24).optional()
);
/** A stage keeps full artwork metadata and renditions, while authoring instructions stay on the owner's record. */
export const zShowImage = z.preprocess((value) => {
  if (!value || typeof value !== "object") return value;
  const { prompt: _prompt, posePlan: _plan, poseAnalysis: _analysis,
    error: _error, errorCode: _code, choreos, ...image } = value as Record<string, any>;
  const projected = sheetChoreos.parse(choreos);
  return { ...image, ...(projected ? { choreos: projected } : {}) };
}, zImageReference);
export const zShowPersona = z.object({
  id: z.string().max(300),
  name: z.string().max(200).optional(),
  relativeScale: scale.optional(),
  image: zShowImage.optional(),
  imageVariations: z.record(zShowImage).optional(),
});
export const zShowAppearance = z.object({
  persona: zShowPersona,
  variants: z.record(zShowPersona),
});
export type ShowAppearance = z.infer<typeof zShowAppearance>;
export interface ShowCastSnapshot {
  revision: number;
  updatedAt: number;
  characters: Record<string, ShowAppearance>;
}

/** The two render-only poses offered before a guest joins a show. */
export interface ShowPosePreview {
  url: string;
  pose: string;
  sheet?: { width: number; height: number };
  sprite?: { left: number; top: number; width: number; height: number };
  frame?: { left: number; top: number; width: number; height: number };
  scale?: number;
}
export interface ShowCharacterChoice {
  id: string;
  displayName: string;
  color?: string;
  peek?: ShowPosePreview;
  excited?: ShowPosePreview;
}

type ChoiceRole = Pick<ShowCharacterChoice, "id" | "displayName" | "color"> & { speakPose?: string };
type PreviewEntry = { preview: ShowPosePreview; semantic?: string };

function posePreviews(persona: z.infer<typeof zShowPersona>): PreviewEntry[] {
  const entries: PreviewEntry[] = [];
  const images = [
    ...Object.entries(persona.imageVariations ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    ...(persona.image ? [["default", persona.image] as const] : []),
  ];
  for (const [key, image] of images) {
    const url = image.large ?? image.original ?? image.small;
    if (!url) continue;
    const add = (name: string, detail?: z.infer<typeof panel>, grid?: z.infer<typeof panelGrid>) => {
      if (detail?.disabled) return;
      if (detail && grid) {
        // A corrupt crop must not reveal neighbouring poses or produce a huge
        // background image. Frames can extend beyond a sheet; sprites cannot.
        const { sprite, frame } = detail;
        if (
          grid.width > 100_000 ||
          grid.height > 100_000 ||
          sprite.width <= 0 ||
          sprite.height <= 0 ||
          sprite.left < 0 ||
          sprite.top < 0 ||
          sprite.left + sprite.width > grid.width ||
          sprite.top + sprite.height > grid.height ||
          frame.width <= 0 ||
          frame.height <= 0 ||
          frame.width > 100_000 ||
          frame.height > 100_000 ||
          Math.abs(frame.left) > 100_000 ||
          Math.abs(frame.top) > 100_000
        )
          return;
      }
      entries.push({
        semantic: detail?.pose,
        preview: {
          url,
          pose: name,
          scale: (persona.relativeScale ?? 1) * (image.relativeScale ?? 1) * (detail?.relativeScale ?? 1),
          ...(detail && grid
            ? {
                sheet: { width: grid.width, height: grid.height },
                sprite: {
                  left: detail.sprite.left,
                  top: detail.sprite.top,
                  width: detail.sprite.width,
                  height: detail.sprite.height,
                },
                frame: {
                  left: detail.frame.left,
                  top: detail.frame.top,
                  width: detail.frame.width,
                  height: detail.frame.height,
                },
              }
            : {}),
        },
      });
    };
    if (image.selectedPanel) {
      add(image.selectedPanel.name, image.selectedPanel, image.selectedPanel);
    } else if (image.panels) {
      for (const [name, detail] of Object.entries(image.panels.details).sort(([a], [b]) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
      ))
        add(name, detail, image.panels);
    } else {
      add(key);
    }
  }
  return entries;
}

/** Project only selection artwork, never a persona, account reference or prompt. */
export function buildShowCharacterChoices(
  roster: readonly ChoiceRole[],
  snapshot?: ShowCastSnapshot | null
): ShowCharacterChoice[] {
  return roster.slice(0, 100).map(({ id, displayName, color, speakPose }) => {
    const parsed = zShowPersona.safeParse(snapshot?.characters[id]?.persona);
    const entries = parsed.success ? posePreviews(parsed.data) : [];
    const token = (name: string | undefined) => entries.find((entry) => entry.preview.pose === name);
    const semantic = (name: string) => entries.find((entry) => entry.semantic === name);
    const peek = token("A1") ?? semantic("peeking") ?? token("default") ?? entries[0];
    const excited =
      semantic("excited") ??
      semantic("laughing") ??
      semantic("greeting") ??
      token(speakPose) ??
      entries.find((entry) => entry !== peek) ??
      peek;
    return {
      id,
      displayName,
      ...(color ? { color } : {}),
      ...(peek ? { peek: peek.preview } : {}),
      ...(excited ? { excited: excited.preview } : {}),
    };
  });
}

/** Admin-only searchable index; never included in audience session documents. */
export interface ShowMonitor {
  id: string;
  accountId: string;
  ownerUid: string;
  title: string;
  status: "active" | "ended";
  createdAt: number;
  updatedAt: number;
  expiresAt: number;
  performanceAccountId?: string;
  performanceId?: string;
  performancePath?: string;
}
